import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Alert, Tooltip } from '@mui/material';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import CustomButton from '../../components/buttons/CustomButton';
import AreaTitle from '../../components/areaTitle/AreaTitle';
import Toast from '../../components/support/Toast';
import PlanUpgradeModal from '../../components/subscription/PlanUpgradeModal';
import { USER_LIMIT_HINT, USER_REACTIVATE_LIMIT_HINT } from '../../components/subscription/planLockTexts';
import { DataProviderInstance } from '../../api/DataProvider';
import { canEditUser, getCurrentUser } from '../../utils/RoleManager';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import useSubscriptionFeatures from '../../hooks/useSubscriptionFeatures';
import { SUBSCRIPTION_LIMITS } from '../../consts/SubscriptionFeatures';
import { USER_ROLES, assignableRolesFor } from '../../constants/userRoles';
import RoleCards from './components/RoleCards';
import HealthRequestsBanner from './components/HealthRequestsBanner';
import TeamList from './components/TeamList';
import UserFormPopup from './components/UserFormPopup';
import TransferOwnershipPopup from './components/TransferOwnershipPopup';
import SeatLimitNotice from './components/SeatLimitNotice';
import { seatUsageFrom } from './components/seatUsage';
import styles from './CompanyUsers.module.css';

// resourceId chega como número (API) ou texto (select); vazio é "sem agenda"
const normalizeId = (value) => (value == null || value === '' ? null : Number(value));

function CompanyUsers() {
  const {
    InviteUser, GetCompanyUsers, RevokeUser, UpdateUser, TransferOwnership, SetHealthProfessional, SetScheduleLink, UserData,
    ReactivateUser, CancelInvite, ResendInvite,
  } =DataProviderInstance();
  const { isOwner, isOwnerOrAdmin } = useRoleAccess();
  const { getLimit } = useSubscriptionFeatures();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  // Agendas da clínica (company_resources) para o dono ligar a cada pessoa
  const [resources, setResources] = useState([]);
  // Vagas como o servidor conta: ativos + convites pendentes válidos
  const [seats, setSeats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Aviso de limite do plano: false | 'create' | 'reactivate' (muda o que fazer no texto)
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  // null = fechado; { user: null } = convite; { user } = edição
  const [form, setForm] = useState(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyUserId, setBusyUserId] = useState(null);
  const [formError, setFormError] = useState('');
  // Erro de vaga do plano: o popup oferece "Ver planos" ao lado do texto
  const [formErrorIsLimit, setFormErrorIsLimit] = useState(false);
  // Linha com ação em andamento (devolver acesso, reenviar ou cancelar convite)
  const [busyKey, setBusyKey] = useState(null);
  // O aviso do sino chega com ?pedido=<userId>: abre a pessoa uma única vez
  const openedFromQueryRef = useRef(false);
  // key nova a cada aviso: o mesmo texto repetido (ex.: limite cheio) aparece de novo a cada clique
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success', key: 0 });

  const loadUsers = useCallback(() => {
    GetCompanyUsers((response) => {
      if (response.error) {
        setError('Não foi possível carregar a equipe. Tente novamente.');
      } else {
        setError(null);
        setUsers(response.users || []);
        setResources(response.resources || []);
        setSeats(response.seats || null);
      }
      setLoading(false);
    });
  }, [GetCompanyUsers]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const showToast = (message, severity = 'success') => (
    setToast((prev) => ({ open: true, message, severity, key: prev.key + 1 }))
  );

  // O clique que abre um aviso não pode fechá-lo como "clique fora" (clickaway)
  const closeToast = (event, reason) => {
    if (reason === 'clickaway') return;
    setToast((prev) => ({ ...prev, open: false }));
  };

  const userLimit = getLimit(SUBSCRIPTION_LIMITS.USERS);
  const activeUsers = users.filter((u) => u.status === 'active');
  const seatUsage = seatUsageFrom(seats, users);
  const isAtUserLimit = userLimit !== null && seatUsage.total >= userLimit;
  const canManage = isOwnerOrAdmin();
  const isAccountOwner = isOwner();
  // Papel pela sessão: o perfil (UserData) guarda o papel antigo depois de uma transferência
  const currentRole = getCurrentUser().role;
  const transferCandidates = users.filter(
    (u) => u.type === 'user' && u.status === 'active' && u.id != UserData?.id
  );

  const roleCounts = activeUsers.reduce((acc, u) => {
    // A antiga Recepção conta como Equipe (migration_v2 já converteu; aqui é só reserva)
    const role = u.role === USER_ROLES.COMPANY_SECRETARY ? USER_ROLES.COMPANY_STAFF : u.role;
    acc[role] = (acc[role] || 0) + 1;
    return acc;
  }, {});
  const healthRequests = isAccountOwner
    ? activeUsers.filter((u) => u.type === 'user' && !u.isHealthProfessional && u.healthProfessionalRequestedAt)
    : [];

  const openForm = (user = null) => {
    setFormError('');
    setFormErrorIsLimit(false);
    setForm({ user });
  };

  useEffect(() => {
    const requested = searchParams.get('pedido');
    if (loading || !requested || openedFromQueryRef.current) return;
    openedFromQueryRef.current = true;
    const person = users.find((u) => u.type === 'user' && u.status === 'active' && String(u.id) === requested);
    if (person && isAccountOwner) openForm(person);
    const next = new URLSearchParams(searchParams);
    next.delete('pedido');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, users, searchParams, isAccountOwner]);

  const handleInviteClick = () => {
    if (!canManage) return;
    // Vagas cheias: o mesmo modal de limite das agendas e salas, com o atalho para trocar de plano
    if (isAtUserLimit) {
      setUpgradeModalOpen('create');
      return;
    }
    openForm();
  };

  const canEditMember = (member) => (
    member.id == UserData?.id ? isAccountOwner : canEditUser(currentRole, member.role)
  );

  const handleEdit = (user) => {
    if (canEditMember(user)) openForm(user);
  };

  const finish = (message) => {
    setSaving(false);
    setForm(null);
    setTransferOpen(false);
    showToast(message);
    loadUsers();
  };

  // Negação da API vem como { error: 'Acesso negado', message: 'texto para a pessoa' }
  const errorText = (response, fallback) => {
    if (typeof response?.message === 'string' && response.message) return response.message;
    return typeof response?.error === 'string' ? response.error : fallback;
  };

  const fail = (response, fallback) => {
    setSaving(false);
    setFormError(errorText(response, fallback));
    setFormErrorIsLimit(response?.code === 'BLOCKED_ON_PLAN');
  };

  const handleInvite = (email, role, healthRequested) => {
    setSaving(true);
    setFormError('');
    InviteUser(email, role, healthRequested, (response) => {
      if (response.error) return fail(response, 'Não foi possível enviar o convite. Tente novamente.');
      // E-mail de quem já esteve na equipe: o servidor devolve o acesso em vez de convidar
      if (response.reactivated) return finish(`Acesso devolvido: ${response.name || email}.`);
      finish('Convite enviado.');
    });
  };

  // Papel, marcação e agenda são endpoints separados: salva nessa ordem, parando no primeiro erro
  const handleSave = (user, { role, isHealthProfessional, resourceId, scheduleScope }) => {
    setSaving(true);
    setFormError('');
    const roleChanged = role !== user.role;
    // O pedido pendente é decidido pelos botões do popup; salvar não mexe nele
    const healthChanged = isHealthProfessional !== !!user.isHealthProfessional;
    // Sem a marcação não há agenda nem alcance (a API limpa os dois ao desligar)
    const linkChanged = isHealthProfessional && (
      normalizeId(resourceId) !== normalizeId(user.resourceId) || (scheduleScope || 'all') !== (user.scheduleScope || 'all')
    );

    const saveLink = (message) => {
      if (!linkChanged) return finish(message);
      SetScheduleLink(user.id, normalizeId(resourceId), scheduleScope || 'all', (response) => {
        if (response.error) return fail(response, 'Não foi possível salvar a agenda da pessoa. Tente novamente.');
        finish(healthChanged || roleChanged ? message : 'Agenda e alcance atualizados.');
      });
    };

    const saveHealth = () => {
      if (!healthChanged) return saveLink('Papel atualizado.');
      // O alcance vai junto ao ligar, para o aviso do sino já sair com o texto certo
      SetHealthProfessional(user.id, isHealthProfessional, (response) => {
        if (response.error) return fail(response, 'Não foi possível salvar a marcação. Tente novamente.');
        saveLink(isHealthProfessional ? 'Marcação Profissional de saúde ligada.' : 'Marcação Profissional de saúde desligada.');
      }, isHealthProfessional ? (scheduleScope || 'all') : null);
    };

    if (!roleChanged) return saveHealth();
    UpdateUser(user.id, role, (response) => {
      if (response.error) return fail(response, 'Não foi possível salvar. Tente novamente.');
      saveHealth();
    });
  };

  const decideHealthRequest = (person, approve) => {
    setBusyUserId(person.id);
    SetHealthProfessional(person.id, approve, (response) => {
      setBusyUserId(null);
      if (response.error) {
        showToast(errorText(response, 'Não foi possível salvar. Tente novamente.'), 'error');
        return;
      }
      setForm(null);
      showToast(approve ? `${person.name || person.email} agora tem acesso aos atendimentos.` : 'Pedido recusado.');
      loadUsers();
    });
  };

  // Ações da linha (Sem acesso e convite pendente): resultado e erro sempre em toast;
  // limite do plano no servidor abre o mesmo aviso do botão (limitMode: 'create' | 'reactivate')
  const runRowAction = (member, call, successMessage, fallback, limitMode = 'create') => {
    setBusyKey(`${member.type || 'user'}-${member.id ?? member.email}`);
    call((response) => {
      setBusyKey(null);
      if (response?.code === 'BLOCKED_ON_PLAN') {
        // O servidor também barra no limite (ex.: outra aba usou a vaga antes)
        loadUsers();
        setUpgradeModalOpen(limitMode);
        return;
      }
      if (response.error) {
        showToast(errorText(response, fallback), 'error');
        return;
      }
      showToast(successMessage(response));
      loadUsers();
    });
  };

  const handleReactivate = (member) => (isAtUserLimit ? setUpgradeModalOpen('reactivate') : runRowAction(
    member,
    (cb) => ReactivateUser(member.id, cb),
    (response) => `Acesso devolvido: ${response.name || member.name || member.email}.`,
    'Não foi possível devolver o acesso. Tente novamente.',
    'reactivate'
  ));

  const handleResendInvite = (member) => runRowAction(
    member,
    (cb) => ResendInvite(member.id, cb),
    () => `Convite reenviado para ${member.email}.`,
    'Não foi possível reenviar o convite. Tente novamente.'
  );

  const handleCancelInvite = (member) => runRowAction(
    member,
    (cb) => CancelInvite(member.id, cb),
    () => `Convite para ${member.email} cancelado. A vaga ficou livre.`,
    'Não foi possível cancelar o convite. Tente novamente.'
  );

  const handleRevoke = (user) => {
    setSaving(true);
    setFormError('');
    RevokeUser(user.id, (response) => {
      if (response.error) return fail(response, 'Não foi possível remover o acesso. Tente novamente.');
      finish('Acesso removido.');
    });
  };

  const openTransfer = () => {
    setFormError('');
    setTransferOpen(true);
  };

  const handleTransfer = (target, password) => {
    setSaving(true);
    setFormError('');
    TransferOwnership(target.id, password, (response) => {
      if (response.error) return fail(response, 'Não foi possível transferir a conta. Tente novamente.');
      finish(`${target.name || target.email} agora é Proprietário(a) da conta.`);
    });
  };

  const inviteBlockedReason = !canManage
    ? 'Só o(a) Proprietário(a) e administradores convidam pessoas'
    : '';

  const editDeniedReason = currentRole === USER_ROLES.COMPANY_ADMIN
    ? 'Só o(a) Proprietário(a) mexe no acesso de administradores'
    : 'Só o(a) Proprietário(a) e administradores mexem na equipe';

  return (
    <div className='page-content'>
      <div className={styles.container}>
        {isAtUserLimit && (
          <SeatLimitNotice usage={seatUsage} limit={userLimit} onUpgradeClick={() => setUpgradeModalOpen('create')} />
        )}
        <PlanUpgradeModal
          isOpen={!!upgradeModalOpen}
          onClose={() => setUpgradeModalOpen(false)}
          featureName="usuários"
          featureNameSingular="usuário"
          type="limit"
          limit={userLimit}
          hint={upgradeModalOpen === 'reactivate' ? USER_REACTIVATE_LIMIT_HINT : USER_LIMIT_HINT}
        />

        <div className={styles.headerContainer}>
          <AreaTitle
            Title="Usuários"
            Icon={<PeopleOutlinedIcon />}
            Description="Quem da equipe entra na Gabriela e o que cada pessoa acessa."
          />
          <Tooltip title={inviteBlockedReason} arrow>
            <span>
              <CustomButton
                icon={<PersonAddOutlinedIcon />}
                label="Convidar pessoa"
                style="main"
                variant="contained"
                onClick={handleInviteClick}
                enabled={canManage}
              />
            </span>
          </Tooltip>
        </div>

        {error && <Alert severity="error">{error}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <HealthRequestsBanner
              requests={healthRequests}
              busyUserId={busyUserId}
              onApprove={(person) => decideHealthRequest(person, true)}
              onDecline={(person) => decideHealthRequest(person, false)}
            />
            <RoleCards counts={roleCounts} onTransfer={isAccountOwner ? openTransfer : null} />
            <TeamList
              members={users}
              currentUserId={UserData?.id}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              resources={resources}
              canEdit={canEditMember}
              canEditSelf={isAccountOwner}
              editDeniedReason={editDeniedReason}
              busyKey={busyKey}
              onEdit={handleEdit}
              onReactivate={canManage ? handleReactivate : null}
              onResendInvite={canManage ? handleResendInvite : null}
              onCancelInvite={canManage ? handleCancelInvite : null}
            />
          </>
        )}

        {form && (
          <UserFormPopup
            user={form.user}
            assignableRoles={assignableRolesFor(currentRole)}
            canSetHealth={isAccountOwner}
            canRequestHealth={canManage && !isAccountOwner}
            resources={resources}
            saving={saving}
            error={formError}
            onUpgrade={formErrorIsLimit ? () => { setForm(null); setUpgradeModalOpen('create'); } : null}
            onClose={() => setForm(null)}
            onInvite={handleInvite}
            onSave={handleSave}
            onRevoke={handleRevoke}
            onDecideRequest={isAccountOwner ? decideHealthRequest : null}
          />
        )}

        {transferOpen && (
          <TransferOwnershipPopup
            candidates={transferCandidates}
            saving={saving}
            error={formError}
            onClose={() => setTransferOpen(false)}
            onConfirm={handleTransfer}
          />
        )}

        <Toast
          key={toast.key}
          open={toast.open}
          onClose={closeToast}
          message={toast.message}
          severity={toast.severity}
          autoHideDuration={6000}
        />
      </div>
    </div>
  );
}

export default CompanyUsers;
